import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Check, ArrowRight } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { pricingPlans, faqData } from '../mock/data';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import SEO from '../components/SEO';

const subscriptionsJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: 'Abonnement entretien et réparation automobile',
  provider: {
    '@type': 'Organization',
    name: 'CarMaintenance',
    url: 'https://car-main.ch/',
  },
  areaServed: { '@type': 'Country', name: 'Switzerland' },
  audience: { '@type': 'Audience', audienceType: 'Propriétaires de véhicules en Suisse' },
  offers: [
    {
      '@type': 'Offer',
      name: 'Abonnement S',
      priceCurrency: 'CHF',
      description: 'Couverture moteur, boîte de vitesses et boîte de transfert — 12 mois.',
    },
    {
      '@type': 'Offer',
      name: 'Abonnement M',
      priceCurrency: 'CHF',
      description: 'Couverture étendue : transmission, direction, freinage, carburant, refroidissement — 24 mois.',
    },
    {
      '@type': 'Offer',
      name: 'Abonnement L',
      priceCurrency: 'CHF',
      description: 'Couverture la plus complète, incluant pièces d\'usure et électronique — 24 mois.',
    },
  ],
};

const Subscriptions = () => {
  // État pour le plan et la franchise sélectionnés
  const [selectedPlan, setSelectedPlan] = useState('L');
  const [selectedFranchise, setSelectedFranchise] = useState(0);
  
  // État pour les pannes sélectionnées
  const [selectedRepairs, setSelectedRepairs] = useState({
    service: true,
    gearbox: true,
    waterPump: true,
    window: false,
    brakes: true
  });

  // Définition des services et leur couverture par plan
  const services = {
    service: {
      name: 'Service annuel',
      cost: selectedPlan === 'S' ? 600 : 1200, // 600 CHF pour S (1 an), 1200 CHF pour M/L (2 ans)
      coveredBy: ['S', 'M', 'L'], // Couvert par tous les plans
      category: 'Entretien courant',
      note: selectedPlan === 'S' ? '(1 an)' : '(2 ans à 600.- annuel)'
    },
    gearbox: {
      name: 'Panne boîte à vitesses',
      cost: 2500,
      coveredBy: ['S', 'M', 'L'], // Couvert par tous les plans
      category: 'Boîte à vitesses'
    },
    waterPump: {
      name: 'Fuite pompe à eau',
      cost: 1400,
      coveredBy: ['M', 'L'], // Système de refroidissement = M et L
      category: 'Système de refroidissement'
    },
    window: {
      name: 'Lève-vitre',
      cost: 600,
      coveredBy: ['L'], // Installation électrique = L uniquement
      category: 'Installation électrique'
    },
    brakes: {
      name: 'Freins AV/AR',
      cost: 1200,
      coveredBy: ['L'], // Pièces d'usure = L uniquement
      category: 'Pièces d\'usure'
    }
  };

  // Prix mensuels des abonnements
  const monthlyPrices = {
    S: { 0: 79, 500: 29 },
    M: { 0: 99, 500: 49 },
    L: { 0: 129, 500: 79 }
  };

  // Durée d'engagement selon le plan
  const commitmentMonths = {
    S: 12,
    M: 24,
    L: 24
  };

  // Filtrer les services couverts par le plan sélectionné
  const availableServices = Object.entries(services).filter(([key, service]) => 
    service.coveredBy.includes(selectedPlan)
  );

  // Toggle d'une réparation
  const toggleRepair = (key) => {
    setSelectedRepairs(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Calculs
  const monthlyPrice = monthlyPrices[selectedPlan][selectedFranchise];
  const months = commitmentMonths[selectedPlan];
  const totalSubscriptionCost = monthlyPrice * months;

  // Coût total sans abonnement (uniquement les pannes sélectionnées ET couvertes par le plan)
  const totalWithoutSub = availableServices.reduce((total, [key, service]) => {
    if (selectedRepairs[key]) {
      return total + service.cost;
    }
    return total;
  }, 0);

  // Coût total avec abonnement
  const franchiseCosts = availableServices.reduce((total, [key, service]) => {
    if (selectedRepairs[key]) {
      return total + selectedFranchise; // Ajoute la franchise si la panne est sélectionnée
    }
    return total;
  }, 0);
  const totalWithSub = totalSubscriptionCost + franchiseCosts;

  // Économies
  const savings = totalWithoutSub - totalWithSub;
  
  // Coût réel par mois = (Abonnement total - Coût des pannes cochées sans abo) / Nb mois
  const realCostPerMonth = ((totalSubscriptionCost + franchiseCosts - totalWithoutSub) / months).toFixed(0);

  return (
    <div className="pt-20">
      <SEO
        title="Abonnements CarMaintenance — Entretien & réparations tout inclus en Suisse"
        description="Choisissez votre abonnement S, M ou L : entretien, pannes mécaniques, pièces d'usure et tarifs préférentiels. Un prix fixe, zéro surprise, partout en Suisse."
        path="/abonnements"
        image="/generated/hero-swiss-road.jpg"
        jsonLd={subscriptionsJsonLd}
      />
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#e8f5ef] via-[#f0f9f4] to-white">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 animate-in fade-in slide-in-from-bottom duration-700">
            Nos abonnements
          </h1>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Découvrez nos <span className="text-[#52a675]">abonnements</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              L'entretien auto réinventé : une formule accessible, modulable et sans frais cachés, pour une prise en charge claire, simple et sans surprise
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto mb-20">
            {pricingPlans.map((plan, index) => (
              <Card
                key={plan.id}
                className={`relative overflow-hidden transition-all duration-300 hover:shadow-2xl ${
                  plan.popular
                    ? 'border-[#52a675] border-2 shadow-xl scale-105'
                    : 'border-gray-200 hover:border-[#52a675]'
                }`}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-[#52a675] text-white px-4 py-1 text-sm font-semibold rounded-bl-lg">
                    Populaire
                  </div>
                )}

                <CardHeader className="space-y-4 pb-8">
                  <CardTitle className="text-2xl font-bold text-gray-900">
                    {plan.name}
                  </CardTitle>
                  <div className="flex items-baseline space-x-2">
                    <span className="text-sm text-gray-500">Dès</span>
                    <span className="text-5xl font-bold text-[#52a675]">{plan.price}</span>
                    <span className="text-gray-600">
                      {plan.currency}/{plan.period}
                    </span>
                  </div>
                </CardHeader>

                <CardContent className="space-y-3">
                  {plan.features.map((feature, idx) => (
                    <div
                      key={idx}
                      className={`flex items-start space-x-3 ${
                        feature.indent ? 'ml-6' : ''
                      } ${
                        feature.isHeader ? 'mt-4 font-semibold text-gray-900' : 'text-gray-600'
                      }`}
                    >
                      {!feature.isHeader && (
                        <Check className="w-5 h-5 text-[#52a675] flex-shrink-0 mt-0.5" />
                      )}
                      <span className="text-sm leading-relaxed">{feature.text}</span>
                    </div>
                  ))}
                </CardContent>

                <CardFooter className="pt-6">
                  <Link to={`/calculateur/${plan.id.toUpperCase()}`} className="w-full">
                    <Button
                      className={`w-full py-6 group transition-all duration-300 ${
                        plan.popular
                          ? 'bg-[#52a675] hover:bg-[#3d8a5c] text-white shadow-lg'
                          : 'bg-gray-100 hover:bg-[#52a675] text-gray-900 hover:text-white'
                      }`}
                    >
                      {plan.ctaText}
                      <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                </CardFooter>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Interactive Savings Calculator */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Calculateur <span className="text-[#52a675]">d'économies</span> interactif
              </h2>
              <p className="text-lg text-gray-600">
                Découvrez combien vous pouvez économiser sur 24 mois avec nos abonnements
              </p>
            </div>

            {/* Sélection du plan et de la franchise */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4 md:p-6 mb-6">
              {/* Boutons plan + franchise */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2.5">Choisissez votre plan</h3>
                  <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Plan d'abonnement">
                    {['S', 'M', 'L'].map((plan) => (
                      <button
                        key={plan}
                        type="button"
                        role="radio"
                        aria-checked={selectedPlan === plan}
                        onClick={() => setSelectedPlan(plan)}
                        className={`py-2.5 px-3 rounded-lg text-sm font-semibold transition-all ${
                          selectedPlan === plan
                            ? 'bg-[#52a675] text-white shadow ring-2 ring-[#52a675]/30'
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                        }`}
                        data-testid={`calc-plan-${plan}`}
                      >
                        Plan {plan}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className="text-sm font-semibold text-gray-700 mb-2.5">Choisissez votre franchise</h3>
                  <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Franchise">
                    {[
                      { value: 0, label: 'Sans franchise' },
                      { value: 500, label: 'Franchise 500 CHF' },
                    ].map((f) => (
                      <button
                        key={f.value}
                        type="button"
                        role="radio"
                        aria-checked={selectedFranchise === f.value}
                        onClick={() => setSelectedFranchise(f.value)}
                        className={`py-2.5 px-3 rounded-lg text-sm font-semibold transition-all ${
                          selectedFranchise === f.value
                            ? 'bg-[#52a675] text-white shadow ring-2 ring-[#52a675]/30'
                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border border-gray-200'
                        }`}
                        data-testid={`calc-franchise-${f.value}`}
                      >
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Récap compact — 2 cols sur mobile, 4 sur desktop */}
              <div className="bg-[#52a675]/5 border border-[#52a675]/20 rounded-xl px-4 py-3 mb-5" data-testid="calc-recap">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                  <div className="text-center">
                    <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-0.5">Plan</p>
                    <p className="text-base md:text-lg font-bold text-gray-900">Abo. {selectedPlan}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-0.5">Engagement</p>
                    <p className="text-base md:text-lg font-bold text-gray-900">{months} mois</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-0.5">Prix / mois</p>
                    <p className="text-base md:text-lg font-bold text-gray-900">{monthlyPrice} CHF</p>
                  </div>
                  <div className="text-center">
                    <p className="text-[11px] uppercase tracking-wide text-gray-500 mb-0.5">Économies / {months} mois</p>
                    <p className="text-lg md:text-xl font-extrabold text-[#2f8757]" data-testid="calc-savings-recap">
                      {savings > 0 ? `${savings.toFixed(0)}` : '0'} CHF
                    </p>
                  </div>
                </div>
              </div>

              {/* === DESKTOP: tableau compact === */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm" data-testid="calc-table-desktop">
                  <thead>
                    <tr className="border-b border-gray-200 text-gray-600">
                      <th className="text-left py-2 px-3 font-medium">
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={availableServices.every(([key]) => selectedRepairs[key])}
                            onChange={(e) => {
                              const newState = {};
                              availableServices.forEach(([key]) => { newState[key] = e.target.checked; });
                              setSelectedRepairs(newState);
                            }}
                            className="w-4 h-4 accent-[#52a675]"
                            aria-label="Tout cocher"
                          />
                          <span>Panne anticipée</span>
                        </label>
                      </th>
                      <th className="text-right py-2 px-3 font-medium text-gray-500 text-xs uppercase tracking-wide">Sans abo</th>
                      <th className="text-right py-2 px-3 font-medium text-[#52a675] text-xs uppercase tracking-wide">
                        Avec {selectedPlan}{selectedFranchise > 0 ? ` (fr. ${selectedFranchise})` : ''}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {availableServices.map(([key, service]) => {
                      const isService = key === 'service';
                      const checked = isService ? true : !!selectedRepairs[key];
                      return (
                        <tr
                          key={key}
                          className={`border-b border-gray-100 transition-colors ${checked ? 'bg-[#52a675]/[0.03]' : ''}`}
                        >
                          <td className="py-2.5 px-3">
                            <label className={`flex items-center gap-2.5 ${isService ? 'cursor-default' : 'cursor-pointer'}`}>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => !isService && toggleRepair(key)}
                                disabled={isService}
                                className="w-4 h-4 accent-[#52a675] transition-transform duration-150 active:scale-90"
                                data-testid={`calc-row-${key}`}
                              />
                              <span className="flex flex-col leading-tight">
                                <span className="text-gray-900 font-medium">
                                  {service.name}
                                  {service.note && <span className="text-[11px] text-gray-400 ml-2 font-normal">{service.note}</span>}
                                </span>
                                <span className="text-[11px] text-gray-500">{service.category}</span>
                              </span>
                            </label>
                          </td>
                          <td className="text-right py-2.5 px-3 text-gray-700 tabular-nums">{service.cost} CHF</td>
                          <td className="text-right py-2.5 px-3 text-[#52a675] font-semibold tabular-nums">{selectedFranchise} CHF</td>
                        </tr>
                      );
                    })}

                    {availableServices.length === 0 && (
                      <tr><td colSpan="3" className="text-center py-6 text-gray-500">Aucun service disponible pour ce plan</td></tr>
                    )}

                    <tr className="border-t border-gray-200 text-gray-700">
                      <td className="py-2.5 px-3">Coût abonnement ({months} mois)</td>
                      <td className="text-right py-2.5 px-3"></td>
                      <td className="text-right py-2.5 px-3 text-[#52a675] font-semibold tabular-nums">{totalSubscriptionCost} CHF</td>
                    </tr>
                    <tr className="bg-gray-50 font-semibold">
                      <td className="py-2.5 px-3 text-gray-900">Total à payer ({months} mois)</td>
                      <td className="text-right py-2.5 px-3 text-gray-900 tabular-nums">{totalWithoutSub} CHF</td>
                      <td className="text-right py-2.5 px-3 text-[#52a675] tabular-nums">{totalWithSub} CHF</td>
                    </tr>
                    <tr className="bg-[#52a675] text-white">
                      <td className="py-3 px-3 rounded-bl-lg font-semibold">
                        {savings > 0 ? `💰 Vos économies sur ${months} mois` : `🛡️ Coût réel : ${realCostPerMonth} CHF/mois`}
                      </td>
                      <td className="text-right py-3 px-3"></td>
                      <td className="text-right py-3 px-3 rounded-br-lg font-bold tabular-nums">
                        {savings > 0
                          ? <span className="text-xl">{savings.toFixed(0)} CHF</span>
                          : <span className="text-sm font-normal">Vous profitez de toutes les couvertures de l'abo {selectedPlan}</span>}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* === MOBILE: cartes verticales === */}
              <div className="md:hidden space-y-2.5" data-testid="calc-cards-mobile">
                {/* "Tout cocher" */}
                <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                  <label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={availableServices.every(([key]) => selectedRepairs[key])}
                      onChange={(e) => {
                        const newState = {};
                        availableServices.forEach(([key]) => { newState[key] = e.target.checked; });
                        setSelectedRepairs(newState);
                      }}
                      className="w-4 h-4 accent-[#52a675]"
                    />
                    Tout cocher
                  </label>
                  <span className="text-[11px] text-gray-500">{availableServices.filter(([k]) => selectedRepairs[k] || k === 'service').length}/{availableServices.length} sélectionnés</span>
                </div>

                {availableServices.map(([key, service]) => {
                  const isService = key === 'service';
                  const checked = isService ? true : !!selectedRepairs[key];
                  return (
                    <label
                      key={key}
                      className={`block rounded-xl border transition-all duration-200 ${
                        checked
                          ? 'border-[#52a675]/40 bg-[#52a675]/5 shadow-sm'
                          : 'border-gray-200 bg-white'
                      } ${isService ? '' : 'active:scale-[0.99] cursor-pointer'}`}
                      data-testid={`calc-card-${key}`}
                    >
                      <div className="flex items-start gap-2.5 px-3 pt-3">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => !isService && toggleRepair(key)}
                          disabled={isService}
                          className="mt-0.5 w-4 h-4 accent-[#52a675] transition-transform duration-150 active:scale-90"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 leading-tight">
                            {service.name}
                            {service.note && <span className="text-[11px] text-gray-400 ml-1 font-normal">{service.note}</span>}
                          </p>
                          <p className="text-[11px] text-gray-500 mt-0.5">{service.category}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 px-3 py-2.5 mt-2 border-t border-gray-100">
                        <div>
                          <p className="text-[10px] uppercase tracking-wide text-gray-500">Sans abo</p>
                          <p className="text-sm font-bold text-gray-800 tabular-nums">{service.cost} CHF</p>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] uppercase tracking-wide text-[#52a675]/80">Avec abo</p>
                          <p className="text-sm font-bold text-[#52a675] tabular-nums">{selectedFranchise} CHF</p>
                        </div>
                      </div>
                    </label>
                  );
                })}

                {availableServices.length === 0 && (
                  <p className="text-center py-6 text-sm text-gray-500">Aucun service disponible pour ce plan</p>
                )}

                {/* Totaux mobile */}
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 space-y-1.5 mt-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-700">Coût abonnement ({months} mois)</span>
                    <span className="text-[#52a675] font-semibold tabular-nums">{totalSubscriptionCost} CHF</span>
                  </div>
                  <div className="flex justify-between text-sm font-semibold border-t border-gray-200 pt-1.5">
                    <span className="text-gray-900">Total ({months} mois)</span>
                    <span className="text-gray-900 tabular-nums">
                      {totalWithoutSub} → <span className="text-[#52a675]">{totalWithSub}</span> CHF
                    </span>
                  </div>
                </div>

                {/* Bandeau économies mobile */}
                <div className="bg-[#52a675] text-white rounded-xl p-4 text-center mt-2 shadow-md">
                  {savings > 0 ? (
                    <>
                      <p className="text-xs uppercase tracking-wide opacity-90">💰 Économies sur {months} mois</p>
                      <p className="text-3xl font-extrabold mt-1 tabular-nums" data-testid="calc-savings-mobile">{savings.toFixed(0)} CHF</p>
                    </>
                  ) : (
                    <>
                      <p className="text-xs uppercase tracking-wide opacity-90">🛡️ Coût réel</p>
                      <p className="text-2xl font-extrabold mt-1 tabular-nums">{realCostPerMonth} CHF/mois</p>
                      <p className="text-xs opacity-90 mt-1">Vous profitez de toutes les couvertures de l'abo {selectedPlan}</p>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="text-center">
              <p className="text-gray-600 mb-2">
                💡 <strong>Conseil :</strong> Cochez les pannes que vous anticipez pour voir vos économies potentielles
              </p>
              <p className="text-sm text-gray-500 mb-6">
                * Les services affichés dépendent de la couverture de votre plan sélectionné
              </p>
              <Link to={`/calculateur/${selectedPlan}`}>
                <Button
                  size="lg"
                  className="bg-[#52a675] hover:bg-[#3d8a5c] text-white px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg shadow-lg hover:shadow-xl transition-all duration-300 group whitespace-normal h-auto max-w-full"
                >
                  Commencer ma souscription avec le plan {selectedPlan}
                  <ArrowRight className="ml-2 w-5 h-5 shrink-0 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Sections */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto mb-20">
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">
                Abonnement <span className="text-[#52a675]">tout inclus</span>
              </h2>
              <h3 className="text-2xl font-semibold text-gray-700">
                L'abonnement CarMaintenance
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                C'est une nouvelle façon de gérer votre véhicule. Vous bénéficiez d'une couverture complète pour l'entretien courant (vidanges, révisions, pièces d'usure) et les imprévus mécaniques (pannes, réparations importantes).
              </p>
              <div className="pt-4">
                <p className="text-xl font-semibold text-gray-900 mb-2">
                  Roulez l'esprit tranquille
                </p>
                <p className="text-gray-600">
                  Avec un service tout inclus, pensé pour vous.
                </p>
              </div>
              <Button
                className="bg-[#52a675] hover:bg-[#3d8a5c] text-white group"
                size="lg"
              >
                Découvrir nos abonnements
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
            <div className="relative">
              <img
                src="/generated/conducteur-serein.jpg"
                alt="Conducteur serein au volant — paysage suisse"
                className="rounded-3xl shadow-2xl"
              />
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto mb-20">
            <div className="relative order-2 lg:order-1">
              <img
                src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=600&h=600&fit=crop"
                alt="Budget control"
                className="rounded-3xl shadow-2xl"
              />
            </div>
            <div className="space-y-6 order-1 lg:order-2">
              <h2 className="text-4xl font-bold text-gray-900">
                Budget <span className="text-[#52a675]">maîtrisé</span>
              </h2>
              <h3 className="text-2xl font-semibold text-gray-700">
                Avec CarMaintenance
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                vous choisissez votre mensualité selon votre budget. Quel que soit l'abonnement choisi, l'entretien de votre véhicule est entièrement pris en charge.
                Grâce à notre système de franchise modulable, vous définissez le montant que vous êtes prêt à dépenser en cas de panne, pour garder des mensualités adaptées à votre budget.
              </p>
              <div className="pt-4">
                <p className="text-xl font-semibold text-gray-900 mb-2">
                  Entretenez votre véhicule
                </p>
                <p className="text-gray-600">
                  sans vous ruiner, et prolongez sa durée.
                </p>
              </div>
              <Button
                className="bg-[#52a675] hover:bg-[#3d8a5c] text-white group"
                size="lg"
              >
                Découvrir nos abonnements
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto mb-20">
            <div className="space-y-6">
              <h2 className="text-4xl font-bold text-gray-900">
                Véhicules neufs et <span className="text-[#52a675]">d'occasion</span>
              </h2>
              <h3 className="text-2xl font-semibold text-gray-700">
                CarMaintenance couvre tous les véhicules
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                qu'ils soient neufs, d'occasion ou importés. Que vous veniez d'acquérir une voiture en Suisse ou en France voisine, notre abonnement s'adapte à votre situation.
              </p>
              <div className="pt-4">
                <p className="text-xl font-semibold text-gray-900 mb-2">
                  Un service
                </p>
                <p className="text-gray-600">
                  simple, fiable et accessible, quelle que soit l'origine de votre voiture.
                </p>
              </div>
              <Button
                className="bg-[#52a675] hover:bg-[#3d8a5c] text-white group"
                size="lg"
              >
                Découvrir nos abonnements
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
            <div className="relative">
              <img
                src="/generated/parking-garage-ch.jpg"
                alt="Parking d'un garage moderne en Suisse"
                className="rounded-3xl shadow-2xl"
              />
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center max-w-6xl mx-auto">
            <div className="relative order-2 lg:order-1">
              <img
                src="/generated/mecanicien-cles.jpg"
                alt="Mécanicien remettant les clés à un client"
                className="rounded-3xl shadow-2xl"
              />
            </div>
            <div className="space-y-6 order-1 lg:order-2">
              <h2 className="text-4xl font-bold text-gray-900">
                Votre confiance, <span className="text-[#52a675]">notre moteur</span>
              </h2>
              <h3 className="text-2xl font-semibold text-gray-700">
                Notre promesse
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed">
                chez CarMaintenance est simple, un service honnête, transparent et sans frais cachés.
              </p>
              <div className="pt-4">
                <p className="text-xl font-semibold text-gray-900 mb-2">
                  Parce que vous méritez
                </p>
                <p className="text-gray-600">
                  un partenaire automobile qui vous respecte, qui inspire confiance et qui prend soin de votre voiture comme il faut.
                </p>
              </div>
              <Button
                className="bg-[#52a675] hover:bg-[#3d8a5c] text-white group"
                size="lg"
              >
                Découvrir nos abonnements
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Sections */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-gradient-to-br from-[#52a675] to-[#3d8a5c] rounded-3xl p-8 text-white shadow-xl">
              <span className="text-sm font-semibold uppercase tracking-wide opacity-90">
                Compte entreprise
              </span>
              <h3 className="text-3xl font-bold mt-4 mb-6">
                Vous souhaitez assurer une flotte de véhicules ?
              </h3>
              <Button variant="secondary" className="bg-white text-[#52a675] hover:bg-gray-100 shadow-lg group">
                Obtenez un devis
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>

            <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl p-8 text-white shadow-xl">
              <span className="text-sm font-semibold uppercase tracking-wide opacity-90">
                Vous êtes garagiste ?
              </span>
              <h3 className="text-3xl font-bold mt-4 mb-6">
                Vous souhaitez devenir garagiste partenaire ?
              </h3>
              <Button variant="secondary" className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg group">
                Contactez-nous
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              FAQs
            </h2>
            <p className="text-lg text-gray-600">
              Vous avez des questions concernant CarMaintenance ? Nous avons rassemblé ici les réponses aux questions les plus fréquentes
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqData.map((faq) => (
              <AccordionItem
                key={faq.id}
                value={faq.id}
                className="border border-gray-200 rounded-lg px-6 hover:border-[#52a675] transition-colors"
              >
                <AccordionTrigger className="text-left font-semibold text-gray-900 hover:text-[#52a675] py-5">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-5 leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">Toutes les questions</p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-br from-[#e8f5ef] to-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
            Une question ? N'hésitez pas à nous contacter.
          </h2>
          <Button
            size="lg"
            className="bg-[#52a675] hover:bg-[#3d8a5c] text-white px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
          >
            Entrer en contact
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Subscriptions;
